import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ArrowBack,
  Backup,
  Launch,
  MoreVert,
  Settings as SettingsIcon,
  Circle,
} from "@mui/icons-material";

const statusStyles = {
  ACTIVE: { color: "#10b981", bg: "#d1fae5", label: "Active" },
  PENDING: { color: "#f59e0b", bg: "#fef3c7", label: "Pending" },
  CREATING: { color: "#3b82f6", bg: "#dbeafe", label: "Creating" },
  SUSPENDED: { color: "#ef4444", bg: "#fee2e2", label: "Suspended" },
  DELETED: { color: "#6b7280", bg: "#f3f4f6", label: "Deleted" },
  MAINTENANCE: { color: "#6366f1", bg: "#e0e7ff", label: "Maintenance" },
};

const envStyles = {
  SANDBOX: { color: "#00c9a7", label: "Sandbox" },
  STAGING: { color: "#ff9800", label: "Staging" },
  PRODUCTION: { color: "#f44336", label: "Production" },
};

/**
 * Site detail page header with actions
 */
const SiteHeader = ({
  site,
  onSettingsClick,
  onBackupClick,
  onMoreClick,
  createLoading,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const status = statusStyles[site?.status] || statusStyles.PENDING;
  const env = envStyles[site?.environment] || envStyles.SANDBOX;
  const protocol =
    process.env.REACT_APP_SSL_ENABLED === "true" ? "https" : "http";
  const visitDomain = site?.customDomain || site?.domain;

  return (
    <Box sx={{ mb: 3 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate("/dashboard/sites")}
        sx={{
          mb: 1.5,
          textTransform: "none",
          color: "text.secondary",
          fontWeight: 500,
        }}
        size="small"
      >
        Back to Sites
      </Button>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
        }}
      >
        {/* Left: Site info */}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 0.5,
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant={isMobile ? "h5" : "h4"}
              sx={{ fontWeight: 700, wordBreak: "break-word" }}
            >
              {site?.name}
            </Typography>
            <Chip
              size="small"
              icon={
                <Circle
                  sx={{
                    fontSize: "8px !important",
                    color: `${status.color} !important`,
                  }}
                />
              }
              label={status.label}
              sx={{
                bgcolor: status.bg,
                color: status.color,
                fontWeight: 600,
                fontSize: "0.75rem",
                height: 24,
                "& .MuiChip-icon": { ml: 0.5 },
              }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.85rem",
                wordBreak: "break-all",
              }}
            >
              {visitDomain}
            </Typography>
            {site?.customDomain && (
              <Chip
                label="Custom Domain"
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.65rem",
                  fontWeight: 600,
                  bgcolor: "#d1fae5",
                  color: "#10b981",
                }}
              />
            )}
            <Chip
              label={env.label}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.65rem",
                fontWeight: 600,
                bgcolor: "action.selected",
              }}
            />
            {site?.versionInfo?.wpVersion && (
              <Chip
                label={`WP ${site.versionInfo.wpVersion
                  .toString()
                  // eslint-disable-next-line no-control-regex
                  .replace(/[\x00-\x1F\x7F-\x9F]/g, "")
                  .replace(/[\s\n\r\t]+/g, "")
                  .trim()}`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.65rem",
                  fontWeight: 500,
                  bgcolor: "action.selected",
                }}
              />
            )}
            {site?.versionInfo?.phpVersion && (
              <Chip
                label={`PHP ${site.versionInfo.phpVersion
                  .toString()
                  // eslint-disable-next-line no-control-regex
                  .replace(/[\x00-\x1F\x7F-\x9F]/g, "")
                  .replace(/[\s\n\r\t]+/g, "")
                  .trim()}`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.65rem",
                  fontWeight: 500,
                  bgcolor: "action.selected",
                }}
              />
            )}
          </Box>

          {site?.customDomain &&
            site?.metadata?.domainVerified &&
            site?.domain && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block" }}
              >
                Also accessible via{" "}
                <Typography
                  component="a"
                  variant="caption"
                  href={`${protocol}://${site.domain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: "primary.main",
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {site.domain}
                </Typography>
              </Typography>
            )}
        </Box>

        {/* Right: Action buttons */}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", flexShrink: 0 }}>
          {!isMobile ? (
            <>
              <Button
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={onSettingsClick}
                disabled={site?.status === "DELETED"}
                size="small"
                disableElevation
                sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
              >
                Settings
              </Button>
              <Button
                variant="outlined"
                startIcon={<Backup />}
                onClick={onBackupClick}
                disabled={createLoading || site?.status === "DELETED"}
                size="small"
                disableElevation
                sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
              >
                {createLoading ? "Creating..." : "Backup"}
              </Button>
              <Button
                variant="contained"
                endIcon={<Launch />}
                onClick={() =>
                  window.open(`${protocol}://${visitDomain}/`, "_blank")
                }
                disabled={site?.status !== "ACTIVE"}
                size="small"
                disableElevation
                sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
              >
                Visit Site
              </Button>
            </>
          ) : (
            <>
              <Tooltip title="Settings">
                <IconButton
                  onClick={onSettingsClick}
                  disabled={site?.status === "DELETED"}
                  size="small"
                >
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Backup">
                <IconButton
                  onClick={onBackupClick}
                  disabled={createLoading || site?.status === "DELETED"}
                  size="small"
                >
                  <Backup fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Visit Site">
                <IconButton
                  onClick={() =>
                    window.open(`${protocol}://${visitDomain}/`, "_blank")
                  }
                  disabled={site?.status !== "ACTIVE"}
                  size="small"
                  color="primary"
                >
                  <Launch fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
          <Tooltip title="More actions">
            <IconButton
              onClick={onMoreClick}
              disabled={site?.status === "DELETED"}
              size="small"
            >
              <MoreVert fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

export default SiteHeader;
